<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-card flat>
          <v-card-text>
            <v-row>
              <v-col cols="12" xl="12" lg="12" md="12" sm="12" xs="12">
                <div class="d-flex flex-row align-start justify-space-between">
                  <div class="d-flex flex-column">
                    <h6 class="text-h6 font-weight-bold">
                      {{ $t("label.final_check") }}
                    </h6>
                    <p class="text-body-2">
                      {{ $t("txt.final_check_tab_description") }}
                    </p>
                  </div>
                  <div class="d-flex flex-row gap-2">
                    <v-btn
                      color="blue-grey lighten-1"
                      class="rounded-lg elevation-0 ml-1 mr-1 font-weight-bold"
                      outlined
                      large
                      @click="exportFullJournalPdf"
                    >
                      <v-progress-circular
                        v-if="isFullPdfLoading"
                        indeterminate
                        color="blue-grey lighten-1"
                        size="20"
                        width="2"
                      ></v-progress-circular>
                      <span v-if="isFullPdfLoading"></span>
                      <span v-else>
                        {{ $t("button.preview") }}
                        <v-icon size="16" class="ml-2">mdi-eye</v-icon>
                      </span>
                    </v-btn>
                    <v-btn
                      color="indigo lighten-1"
                      class="rounded-lg elevation-0 ml-1 mr-1 font-weight-bold"
                      large
                      outlined
                      @click="handleArchiveRegister"
                      :disabled="isArchiveLoading"
                    >
                      <v-progress-circular
                        v-if="isArchiveLoading"
                        indeterminate
                        color="indigo lighten-1"
                        size="20"
                        width="2"
                      ></v-progress-circular>
                      <span v-if="isArchiveLoading"></span>
                      <span v-else>
                        {{ $t("button.register_archive") }}
                        <v-icon size="16" class="ml-2">mdi-archive</v-icon>
                      </span>
                    </v-btn>
                    <v-btn
                      color="red lighten-1"
                      class="rounded-lg elevation-0 ml-1 mr-1 font-weight-bold"
                      large
                      dark
                      @click="handleFinalPublish"
                    >
                      {{ $t("button.final_publish") }}
                      <v-icon size="16" class="ml-2">mdi-check</v-icon>
                    </v-btn>
                  </div>
                </div>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" xl="6" lg="6" md="6" sm="12" xs="12">
                <v-row>
                  <v-col cols="12">
                    <v-card
                      class="rounded-lg overflow-y-auto"
                      height="900"
                      outlined
                    >
                      <v-card-text>
                        <!-- 필수 페이지 섹션 -->
                        <v-card flat outlined class="rounded-lg ma-1 mb-3">
                          <v-card-text class="pa-2">
                            <div class="d-flex flex-row justify-space-between align-center">
                              <div class="d-flex flex-row align-center">
                                <v-sheet
                                  color="indigo darken-1"
                                  class="rounded-lg pa-2 mr-2 d-flex align-center justify-center"
                                  min-width="55"
                                  min-height="55"
                                  elevation="2"
                                >
                                  <v-icon size="24" class="white--text">mdi-file-document-multiple</v-icon>
                                </v-sheet>
                                <div class="d-flex flex-column align-start justify-center">
                                  <span class="text-body-2 font-weight-bold">필수 페이지</span>
                                  <span class="text-body-2">Required Pages</span>
                                </div>
                              </div>
                            </div>
                            <v-sheet color="grey lighten-3" class="mt-2 rounded-b-lg d-flex flex-column text-body-2">
                              <template v-for="(page, index) in fixedPagesList">
                                <!-- 추가된 빈페이지인 경우 -->
                                <div
                                  v-if="page.isBlank"
                                  :key="'fixed-blank-' + index"
                                  class="pa-2 d-flex flex-row justify-space-between align-center"
                                  style="background-color: #e3f2fd;"
                                >
                                  <span class="text-body-2 font-italic grey--text text--darken-1">
                                    <v-icon small class="mr-1">mdi-file-outline</v-icon>
                                    빈페이지 (추가)
                                  </span>
                                  <div class="d-flex align-center">
                                    <span class="text-body-2 mr-2">p. {{ page.page }}</span>
                                    <v-btn
                                      x-small
                                      icon
                                      color="red"
                                      @click="removeFixedBlankPage(index)"
                                    >
                                      <v-icon x-small>mdi-close</v-icon>
                                    </v-btn>
                                  </div>
                                </div>
                                <!-- 일반 고정 페이지인 경우 -->
                                <div
                                  v-else
                                  :key="'fixed-' + index"
                                  class="pa-2 d-flex flex-row justify-space-between align-center"
                                >
                                  <span class="text-body-2">{{ page.title }}</span>
                                  <div class="d-flex align-center">
                                    <span class="text-body-2 mr-2">p. {{ page.page }}</span>
                                    <v-tooltip bottom>
                                      <template v-slot:activator="{ on, attrs }">
                                        <v-btn
                                          x-small
                                          icon
                                          color="blue"
                                          v-bind="attrs"
                                          v-on="on"
                                          @click="addFixedBlankPage(index)"
                                        >
                                          <v-icon x-small>mdi-plus</v-icon>
                                        </v-btn>
                                      </template>
                                      <span>빈페이지 추가</span>
                                    </v-tooltip>
                                  </div>
                                </div>
                              </template>
                              <!-- 뒤쪽 필수 페이지 구분선 -->
                              <v-divider class="my-1"></v-divider>
                              <div class="pa-1 text-caption grey--text text-center">
                                ─ 논문 이후 ─
                              </div>
                              <!-- 뒤쪽 필수 페이지 (속표지(뒷), 백커버) -->
                              <div
                                v-for="(page, index) in fixedPages"
                                :key="'back-' + index"
                                class="pa-2 d-flex flex-row justify-space-between align-center"
                              >
                                <span class="text-body-2">{{ page.title }}</span>
                                <span class="text-body-2">p. {{ page.page }}</span>
                              </div>
                            </v-sheet>
                          </v-card-text>
                        </v-card>

                        <!-- 논문 그룹 섹션 -->
                        <div v-for="group in groups" :key="group.idx">
                          <v-card flat outlined class="rounded-lg ma-1">
                            <v-card-text class="pa-2">
                              <div
                                class="d-flex flex-row justify-space-between align-center"
                              >
                                <div class="d-flex flex-row align-center">
                                  <v-sheet
                                    color="grey darken-4"
                                    class="rounded-lg pa-2 mr-2 d-flex align-center justify-center"
                                    min-width="55"
                                    min-height="55"
                                    elevation="2"
                                  >
                                    <v-icon size="24" class="white--text"
                                      >mdi-book-open-page-variant</v-icon
                                    >
                                  </v-sheet>

                                  <div
                                    class="d-flex flex-column align-start justify-center"
                                  >
                                    <span
                                      class="text-body-2 font-weight-bold"
                                      >{{ group.title }}</span
                                    >
                                    <span class="text-body-2">{{
                                      group.title_eng
                                    }}</span>
                                  </div>
                                </div>
                              </div>

                              <div v-if="group?.contents?.length > 0">
                                <v-sheet
                                  color="grey lighten-3"
                                  class="mt-2 rounded-b-lg d-flex flex-column text-body-2"
                                >
                                  <template v-for="(content, index) in group.contents">
                                    <!-- 빈페이지인 경우 -->
                                    <div
                                      v-if="content.category === 'empty'"
                                      :key="'empty-' + index"
                                      class="pa-2 d-flex flex-row justify-space-between align-center"
                                      style="background-color: #e3f2fd;"
                                    >
                                      <span class="text-body-2 font-italic grey--text text--darken-1">
                                        <v-icon small class="mr-1">mdi-file-outline</v-icon>
                                        빈페이지 (공페이지)
                                      </span>
                                      <div class="d-flex align-center">
                                        <span class="text-body-2 mr-2">
                                          p. {{ content.startPage || '-' }}
                                        </span>
                                        <v-btn
                                          x-small
                                          icon
                                          color="red"
                                          @click="removeBlankPage(group.idx, index)"
                                        >
                                          <v-icon x-small>mdi-close</v-icon>
                                        </v-btn>
                                      </div>
                                    </div>
                                    <!-- 일반 논문인 경우 -->
                                    <template v-else>
                                      <!-- 장표지 (논문 커버 페이지) -->
                                      <div
                                        :key="'cover-' + index"
                                        class="pa-2 d-flex flex-row justify-space-between align-center"
                                        style="background-color: #f5f5f5;"
                                      >
                                        <span class="text-body-2 grey--text text--darken-1">
                                          <v-icon small class="mr-1">mdi-file-document-outline</v-icon>
                                          장표지 (제{{ getPaperNumber(group.contents, index) }}장)
                                        </span>
                                        <span class="text-body-2">p. {{ content.coverPage || '-' }}</span>
                                      </div>
                                      <!-- 논문 본문 -->
                                      <div
                                        :key="'content-' + index"
                                        class="pa-2 d-flex flex-row justify-space-between align-center"
                                      >
                                        <span class="text-body-2">
                                          {{ getPaperNumber(group.contents, index) }}. {{ content.title }}
                                        </span>
                                        <div class="d-flex align-center">
                                          <span class="text-body-2 mr-2">
                                            {{
                                              getPageRange(
                                                content.startPage,
                                                content.endPage
                                              )
                                            }}
                                          </span>
                                          <v-tooltip bottom>
                                            <template v-slot:activator="{ on, attrs }">
                                              <v-btn
                                                x-small
                                                icon
                                                color="blue"
                                                v-bind="attrs"
                                                v-on="on"
                                                @click="addBlankPage(group.idx, index)"
                                              >
                                                <v-icon x-small>mdi-plus</v-icon>
                                              </v-btn>
                                            </template>
                                            <span>빈페이지 추가</span>
                                          </v-tooltip>
                                        </div>
                                      </div>
                                    </template>
                                  </template>
                                </v-sheet>
                              </div>
                            </v-card-text>
                          </v-card>
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="12" xl="6" lg="6" md="6" sm="12" xs="12">
                <!-- 목차/초록 블락 (나중에 사용할 수 있으므로 보관) -->
                <!--
                <div class="d-flex flex-row align-start justify-space-between">
                  <div class="d-flex flex-column">
                    <h6 class="text-h6 font-weight-bold">
                      {{ $t("label.index_abstract") }}
                    </h6>
                    <p class="text-body-2">
                      {{ $t("txt.index_abstract_description") }}
                    </p>
                  </div>
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        color="primary lighten-1"
                        outlined
                        fab
                        small
                        v-bind="attrs"
                        v-on="on"
                        @click="createJournalIndex"
                      >
                        <v-icon size="16">mdi-refresh</v-icon>
                      </v-btn>
                    </template>

                    <p class="text-caption mb-0">
                      {{ $t("txt.initialize_index_description") }}
                    </p>
                  </v-tooltip>
                </div>
                <submission-editor-card
                  :value="journalIndex"
                  @update-content="updateEditorContent"
                  :height="700"
                ></submission-editor-card>
                -->
                <div v-if="groups.length > 0" class="preview-panel final-preview-panel">
                  <div class="preview-header">
                    <span class="text-subtitle-2 font-weight-medium">미리보기</span>
                    <span class="text-caption grey--text">{{ totalPageCount }}페이지</span>
                  </div>
                  <div class="preview-content">
                    <div ref="fullJournalPreview" class="final-preview-content">
                      <full-journal-preview-content
                        v-if="fullPdfPages.length > 0"
                        :journal="journal"
                        :page-size="pageSize"
                        :pages="fullPdfPages"
                        :items="fullPdfItems"
                        :enabled-fixed-pages="isFixedPageEnabled"
                      />
                    </div>
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <!-- PDF 출력용 (저널 전체 레이아웃) -->
    <div ref="fullJournalPdfContent" class="pdf-capture-container">
      <div class="final-preview-panel">
        <div class="final-preview-content">
          <full-journal-preview-content
            v-if="fullPdfPages.length > 0"
            :journal="journal"
            :page-size="pageSize"
            :pages="fullPdfPages"
            :items="fullPdfItems"
            :enabled-fixed-pages="isFixedPageEnabled"
          />
        </div>
      </div>
    </div>
  </v-container>
</template>

<script>
/* eslint-disable no-console */
// import SubmissionEditorCard from "@/components/common/Cards/SubmissionEditorCard.vue";

import {
  journalContentGroupApi,
  journalContentApi,
  journalApi,
  archiveApi,
  fileApi,
} from "@/api/api";
import { confirmPublishPopup } from "@/fields/journal/popups";
import {
  PAGE_DIMENSIONS,
  getContentWidth,
  getPagePadding,
  splitContentByHeight,
} from "@/utils/paperEditor";
import { openIframePrintPreview, generatePdfBlobFromElement } from "@/utils/pdfGenerator";
import { parseContentToAst, renderAstToHtml } from "@/utils/paperAst";
import FullJournalPreviewContent from "@/components/Journal/FullJournalPreviewContent.vue";

const SERVER_URL = process.env.VUE_APP_SERVER_URI;

export default {
  name: "show-final-journal-content",
  components: {
    // SubmissionEditorCard,
    FullJournalPreviewContent,
  },
  props: {
    journal_idx: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      groups: [],
      journalIndex: "",
      journal: {},
      isFullPdfLoading: false,
      isArchiveLoading: false,
      pageSize: "A5",
      fullPdfGroups: [],
      fullPdfPages: [],
      isBuilding: null,
      // 필수 페이지 리스트 (빈페이지 추가 가능)
      fixedPagesList: [
        { id: 'cover', title: '표지', page: 1, isBlank: false },
        { id: 'info', title: '정보페이지', page: 2, isBlank: false },
        { id: 'inner_front', title: '속표지(앞)', page: 3, isBlank: false },
        { id: 'blank', title: '빈페이지', page: 4, isBlank: false },
        { id: 'title', title: '표제지', page: 5, isBlank: false },
        { id: 'toc', title: '목차', page: 6, isBlank: false },
      ],
    };
  },
  methods: {
    /** ****************************************************************************************
     * 버튼 액션
     * ****************************************************************************************/
    async handleFinalPublish() {
      // TODO : 알럿 확인
      const isConfirmed = await confirmPublishPopup();
      if (!isConfirmed) {
        return;
      }

      const isSuccess = await this.requestFinalPublish();

      if (isSuccess) {
        this.notifyVue({
          message: this.$t("alert.success_request"),
          style: "success",
        });
        this.$router.push({
          name: "journal-publish-management",
        });
      }
    },

    /** ****************************************************************************************
     * API 조회
     * ****************************************************************************************/
    async requestFinalPublish() {
      try {
        await this.ensureFullPdfReady();
        const params = {
          journal_idx: this.journal.idx,
        };

        const body = this.buildFinalPublishPayloadFromPreview();
        if (body.length === 0) {
          this.notifyVue({
            message: "최종 발행에 필요한 페이지 정보를 준비하지 못했습니다.",
            style: "warning",
          });
          return false;
        }
        const response = await journalContentApi.finalPublish(params, body);
        if (response.status === 200) {
          return true;
        }
        return false;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
        this.notifyVue({
          message: this.$t("alert.failed_request"),
          style: "danger",
        });
      }
    },
    async handleArchiveRegister() {
      const isSuccess = await this.createArchiveEntryFromPublishedJournal();
      if (isSuccess) {
        this.notifyVue({
          message: this.$t("alert.success_request"),
          style: "success",
        });
      } else {
        this.notifyVue({
          message: this.$t("alert.failed_request"),
          style: "warning",
        });
      }
    },
    getArchiveCategory() {
      const code = this.journal?.journal_type?.code;
      if (code === "procs" || code === "abst") return "C";
      return "J";
    },
    getArchiveBaseName() {
      const journalIdx = this.journal?.idx || this.journal_idx || Date.now();
      const year = this.journal?.year || this.journal?.publish_date?.year;
      const yearSuffix = year ? `_${year}` : "";
      return `journal_${journalIdx}${yearSuffix}`;
    },
    async fetchArchivesByCategory(category) {
      try {
        const params = { category, page: 1, size: 1000 };
        const response = await archiveApi.showAllByCateogory(params);
        const data = response?.data?.response || response?.data || {};
        return Array.isArray(data.elements) ? data.elements : [];
      } catch (error) {
        return [];
      }
    },
    getNextArchiveOrderNumber(archives) {
      const maxOrder = (archives || []).reduce((maxValue, item) => {
        const value = Number(item?.order_number);
        if (Number.isFinite(value) && value > maxValue) return value;
        return maxValue;
      }, 0);
      return maxOrder + 1;
    },
    async uploadArchivePdfBlob(pdfBlob, baseName) {
      const formData = new FormData();
      const fileName = `${baseName}.pdf`;
      const file = new File([pdfBlob], fileName, { type: "application/pdf" });
      formData.append("file", file);
      const response = await fileApi.uploadFile(formData);
      return response?.data?.path || response?.data?.response?.path || response?.data?.data?.path || null;
    },
    async createArchiveEntryFromPublishedJournal() {
      try {
        await this.ensureFullPdfReady();
        const previewElement = this.$refs.fullJournalPdfContent;
        if (!previewElement || this.fullPdfPages.length === 0) {
          return false;
        }

        const category = this.getArchiveCategory();
        const baseName = this.getArchiveBaseName();
        const archives = await this.fetchArchivesByCategory(category);

        const existing = archives.find((item) => {
          const sameTitle = item?.title && this.journal?.title && item.title === this.journal.title;
          const fileName = item?.file_name || "";
          const filePathBase = item?.file_path ? item.file_path.split("/").pop().replace(/\.pdf$/i, "") : "";
          const sameFile = (fileName && fileName === baseName) || (filePathBase && filePathBase === baseName);
          return sameTitle || sameFile;
        });
        if (existing) {
          return true;
        }

        this.isArchiveLoading = true;
        const pdfBlob = await generatePdfBlobFromElement(
          previewElement,
          null,
          this.journal?.page_size || this.pageSize
        );

        const filePath = await this.uploadArchivePdfBlob(pdfBlob, baseName);
        if (!filePath) {
          return false;
        }

        const archivePayload = {
          title: this.journal?.title || baseName,
          down_file_name: this.journal?.title || baseName,
          order_number: this.getNextArchiveOrderNumber(archives),
          category: category,
          file_path: filePath,
        };

        const response = await archiveApi.createArchive(archivePayload);
        return response?.status === 201 || response?.status === 200;
      } catch (error) {
        return false;
      } finally {
        this.isArchiveLoading = false;
      }
    },
    buildFinalPublishPayloadFromPreview() {
      const items = (this.fullPdfItems || [])
        .filter(item => item && item.category !== "empty" && !String(item.idx || "").startsWith("empty-"))
        .map((item) => ({
          ...item,
          start_page: Number(item.start_page ?? item.startPage),
        }))
        .filter((item) => Number.isFinite(item.start_page))
        .sort((a, b) => a.start_page - b.start_page);

      if (items.length === 0) return [];

      const lastPageNumber = (this.fullPdfPages || []).reduce((maxPage, page) => {
        const pageNumber = Number(page?.pageNumber);
        if (Number.isFinite(pageNumber) && pageNumber > maxPage) {
          return pageNumber;
        }
        return maxPage;
      }, 0);

      return items.map((item, index) => {
        const nextStart = items[index + 1]?.start_page;
        let end_page = null;
        if (Number.isFinite(nextStart)) {
          end_page = nextStart - 1;
        } else if (lastPageNumber > 0) {
          end_page = lastPageNumber;
        } else {
          end_page = item.start_page;
        }
        return {
          journal_content_idx: item.idx,
          start_page: item.start_page,
          end_page,
        };
      });
    },

    async getJournalGroups() {
      try {
        const params = {
          journal_idx: this.journal.idx,
        };
        const response = await journalContentGroupApi.showAll(params);
        this.groups = response.data.response;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
        this.notifyVue({
          message: this.$t("alert.failed_request"),
          style: "danger",
        });
      }
    },
    async fetchJournal() {
      try {
        const params = {
          idx: this.journal_idx,
        };
        const response = await journalApi.showJournal(params);
        if (response.status === 200) {
          this.journal = response.data.response;
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
        this.notifyVue({
          message: this.$t("alert.failed_request"),
          style: "danger",
        });
      }
    },
    /** ****************************************************************************************
     * 보조 메서드
     * ****************************************************************************************/
    calculatePageOffsets() {
      let currentPage = 2; // 시작 페이지 번호 (표지 고려 2부터 시작)

      this.groups.forEach((group, index) => {
        group.startPage = currentPage + index; // 각 그룹의 시작 페이지에 index를 더함
        group.totalPages = group.contents.reduce((total, content) => {
          return total + (content.endPage - content.startPage + 1);
        }, 0);
        currentPage += group.totalPages; // 다음 그룹의 시작 페이지 설정
      });
    },
    updatePageRange(data) {
      this.groups.forEach((group) => {
        group.contents.forEach((content, index) => {
          if (content.idx === data.contentIdx) {
            this.$set(group.contents[index], "startPage", data.startPage);
            this.$set(group.contents[index], "endPage", data.endPage);
            // 본문 페이지 수 저장 (장표지 계산용)
            this.$set(group.contents[index], "pageCount", data.pageCount || (data.endPage - data.startPage + 1));
          }
        });
      });
      this.calculatePageOffsets();
      // 장표지 포함 페이지 번호 재계산 (UI 표시용)
      this.recalculateDisplayPageNumbers();
    },
    getPageRange(startPage, endPage) {
      if (startPage === endPage) {
        return `p. ${startPage}`;
      } else {
        return `pp. ${startPage} - ${endPage}`;
      }
    },
    /**
     * 빈페이지 추가 메서드
     * @param groupIdx - 그룹 idx
     * @param contentIndex - 콘텐츠 인덱스 (이 위치 다음에 빈페이지 추가)
     */
    addBlankPage(groupIdx, contentIndex) {
      const groupIndex = this.groups.findIndex(g => g.idx === groupIdx);
      if (groupIndex === -1) return;

      const blankPage = {
        idx: `empty-${Date.now()}`,
        category: 'empty',
        title: '빈페이지',
        startPage: null,
        endPage: null,
      };

      // contentIndex 다음 위치에 빈페이지 삽입
      this.groups[groupIndex].contents.splice(contentIndex + 1, 0, blankPage);

      // 페이지 번호 재계산
      this.recalculatePageNumbers();
    },
    /**
     * 빈페이지 삭제 메서드
     * @param groupIdx - 그룹 idx
     * @param contentIndex - 삭제할 콘텐츠 인덱스
     */
    removeBlankPage(groupIdx, contentIndex) {
      const groupIndex = this.groups.findIndex(g => g.idx === groupIdx);
      if (groupIndex === -1) return;

      const content = this.groups[groupIndex].contents[contentIndex];
      if (content?.category === 'empty') {
        this.groups[groupIndex].contents.splice(contentIndex, 1);

        // 페이지 번호 재계산
        this.recalculatePageNumbers();
      }
    },
    /**
     * 논문 번호 계산 (빈페이지 제외)
     * @param contents - 콘텐츠 배열
     * @param currentIndex - 현재 인덱스
     */
    getPaperNumber(contents, currentIndex) {
      let paperNumber = 0;
      for (let i = 0; i <= currentIndex; i++) {
        if (contents[i].category !== 'empty') {
          paperNumber++;
        }
      }
      return paperNumber;
    },
    /**
     * 페이지 번호 재계산 메서드 (PDF 템플릿용 - 기존 로직 유지)
     */
    recalculatePageNumbers() {
      // 필수 페이지 번호 재계산
      this.recalculateFixedPageNumbers();

      // 필수 페이지 수 (앞쪽)
      const fixedPageCount = this.fixedPagesList.length;
      let currentPage = fixedPageCount + 1;

      this.groups.forEach((group) => {
        group.startPage = currentPage;
        group.contents.forEach((content) => {
          if (content.category === 'empty') {
            content.startPage = currentPage;
            content.endPage = currentPage;
            currentPage += 1;
          } else {
            // 기존 페이지 수 유지
            const contentPageCount = content.pageCount || (content.endPage - content.startPage + 1) || 1;
            content.startPage = currentPage;
            content.endPage = currentPage + contentPageCount - 1;
            currentPage = content.endPage + 1;
          }
        });
      });

      // UI 표시용 장표지 페이지 계산
      this.recalculateDisplayPageNumbers();
    },
    /**
     * UI 표시용 페이지 번호 계산 (장표지 포함)
     * PDF 템플릿의 실제 페이지 번호에는 영향 없음
     */
    recalculateDisplayPageNumbers() {
      // 필수 페이지 수 (앞쪽)
      const fixedPageCount = this.fixedPagesList.length;
      let displayPage = fixedPageCount + 1;

      this.groups.forEach((group) => {
        group.contents.forEach((content) => {
          if (content.category === 'empty') {
            // 빈페이지는 coverPage 없음
            content.coverPage = null;
          } else {
            // 장표지 페이지 번호 (UI 표시용)
            content.coverPage = displayPage;
            displayPage += 1;
            // 본문 페이지 수만큼 증가
            const contentPageCount = content.pageCount || 1;
            displayPage += contentPageCount;
          }
        });
      });
    },
    /**
     * 필수 페이지에 빈페이지 추가
     * @param index - 추가할 위치 (이 위치 다음에 삽입)
     */
    async addFixedBlankPage(index) {
      const blankPage = {
        id: `fixed-blank-${Date.now()}`,
        title: '빈페이지 (추가)',
        page: 0,
        isBlank: true,
      };
      this.fixedPagesList.splice(index + 1, 0, blankPage);
      this.recalculatePageNumbers();
      await this.saveFixedPagesListToStorage();
    },
    /**
     * 필수 페이지에서 빈페이지 삭제
     * @param index - 삭제할 인덱스
     */
    async removeFixedBlankPage(index) {
      if (this.fixedPagesList[index]?.isBlank) {
        this.fixedPagesList.splice(index, 1);
        this.recalculatePageNumbers();
        await this.saveFixedPagesListToStorage();
      }
    },
    /**
     * 필수 페이지 번호 재계산
     */
    recalculateFixedPageNumbers() {
      this.fixedPagesList.forEach((page, index) => {
        page.page = index + 1;
      });
    },
    /**
     * fixedPagesList를 API를 통해 DB에 저장
     */
    async saveFixedPagesListToStorage() {
      try {
        const data = {
          idx: this.journal_idx,
          fixed_pages_info: JSON.stringify(this.fixedPagesList),
        };
        const response = await journalApi.updateJournalFixedPages(data);
        if (response.status === 200) {
          this.notifyVue({
            message: '페이지 구성이 저장되었습니다.',
            style: 'success',
          });
        }
      } catch (error) {
        console.error('Failed to save fixedPagesList:', error);
        this.notifyVue({
          message: '페이지 구성 저장에 실패했습니다.',
          style: 'danger',
        });
      }
    },
    /**
     * journal 데이터에서 fixedPagesList 불러오기
     * EditGroupPapers에서 저장한 enabled 설정을 반영
     */
    loadFixedPagesListFromJournal() {
      try {
        if (this.journal?.fixed_pages_info) {
          const parsed = JSON.parse(this.journal.fixed_pages_info);

          // EditGroupPapers에서 저장한 객체 형식 { id: enabled } 처리
          if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
            // 기본 fixedPagesList에서 enabled가 false인 항목 제거
            const defaultPages = [
              { id: 'cover', title: '표지', page: 1, isBlank: false },
              { id: 'info', title: '정보페이지', page: 2, isBlank: false },
              { id: 'inner_front', title: '속표지(앞)', page: 3, isBlank: false },
              { id: 'blank', title: '빈페이지', page: 4, isBlank: false },
              { id: 'title', title: '표제지', page: 5, isBlank: false },
              { id: 'toc', title: '목차', page: 6, isBlank: false },
            ];
            // enabled가 false가 아닌 페이지만 포함 (기본값 true)
            this.fixedPagesList = defaultPages.filter(page => {
              return parsed[page.id] !== false;
            });
            // 페이지 번호 재계산
            this.recalculateFixedPageNumbers();
          }
          // 배열 형식 (이전 방식) 처리
          else if (Array.isArray(parsed) && parsed.length > 0) {
            this.fixedPagesList = parsed;
          }
        }
      } catch (error) {
        console.error('Failed to parse fixed_pages_info:', error);
      }
    },

    async exportFullJournalPdf() {
      if (this.groups.length === 0) {
        this.notifyVue({ message: "논문 그룹이 없습니다.", style: "warning" });
        return;
      }

      try {
        this.isFullPdfLoading = true;
        await this.buildFullPdfGroups();
        this.buildFullPdfPages();
        await this.$nextTick();

        const previewElement = this.$refs.fullJournalPdfContent;
        if (!previewElement || this.fullPdfPages.length === 0) {
          this.notifyVue({ message: "전체 PDF를 준비하지 못했습니다.", style: "warning" });
          return;
        }

        const tempContainer = document.createElement("div");
        tempContainer.innerHTML = this.processImageUrls(previewElement.innerHTML, {
          addCrossOrigin: false,
        });

        openIframePrintPreview({
          element: tempContainer,
          title: this.journal?.title || "저널 전체 미리보기",
          pageSize: this.journal?.page_size || this.pageSize,
          usePrint: true,
        });
      } catch (error) {
        console.error(error);
        this.notifyVue({ message: "전체 PDF 생성에 실패했습니다.", style: "danger" });
      } finally {
        this.isFullPdfLoading = false;
      }
    },
    updateEditorContent(content) {
      this.journalIndex = content;
    },

    // 목차 생성 메서드
    createJournalIndex() {
      let indexContent = "";

      this.groups.forEach((group, groupIndex) => {
        indexContent += `<p style="font-weight: bold;">${group.title}</p>`;
        group.contents.forEach((content, contentIndex) => {
          // 빈페이지는 목차에서 제외
          if (content.category === 'empty') return;

          indexContent += `<p>${content.title} ${content?.startPage}</p>`;
          indexContent += content.title_eng
            ? `<p>${content.title_eng}</p>`
            : "";
          indexContent += content.subtitle ? `<p>${content?.subtitle}</p>` : "";
          indexContent += content.subtitle_eng
            ? `<p>${content.subtitle_eng}</p>`
            : "";
        });
      });
      this.journalIndex = indexContent;
    },

    /* ****************************************************************************************
     * 알럿
     * ****************************************************************************************/
    notifyVue({
      vertical = "top",
      horizontal = "right",
      message = "",
      style = "primary",
      icon = "priority_high",
      timeout = 5000,
    } = {}) {
      this.$notify({
        timeout: timeout,
        message: message,
        icon: icon,
        type: style,
        horizontalAlign: horizontal,
        verticalAlign: vertical,
      });
    },

    async openFullPreview() {
      await this.ensureFullPdfReady();
      const previewElement = this.$refs.fullJournalPreview;
      if (!previewElement || this.fullPdfPages.length === 0) {
        this.notifyVue({ message: "전체 PDF를 준비하지 못했습니다.", style: "warning" });
        return;
      }
      const panel = previewElement.closest(".final-preview-panel");
      let exportHtml = "";
      if (panel) {
        const clone = panel.cloneNode(true);
        const header = clone.querySelector(".preview-header");
        if (header) header.remove();
        clone.classList.remove("preview-panel");
        exportHtml = clone.outerHTML;
      } else {
        const pdfLayout = previewElement.querySelector(".pdf-full-layout");
        exportHtml = pdfLayout ? pdfLayout.outerHTML : previewElement.innerHTML;
      }
      const tempContainer = document.createElement("div");
      tempContainer.innerHTML = this.processImageUrls(exportHtml, {
        addCrossOrigin: false,
      });
      openIframePrintPreview({
        element: tempContainer,
        title: this.journal?.title || "저널 전체 미리보기",
        pageSize: this.journal?.page_size || this.pageSize,
        usePrint: true,
      });
    },
    async ensureFullPdfReady() {
      if (this.isBuilding) {
        await this.isBuilding;
        return;
      }
      if (this.fullPdfPages.length > 0) return;
      this.isBuilding = (async () => {
        await this.fetchJournal();
        await this.buildFullPdfGroups();
        this.buildFullPdfPages();
        await this.$nextTick();
      })();
      await this.isBuilding;
      this.isBuilding = null;
    },
    processImageUrls(htmlString, { addCrossOrigin = false, preferRelative = false } = {}) {
      if (!htmlString) return htmlString;
      const replaceIskalPath = (match, quote, path) => {
        const normalized = path.startsWith("/") ? path.slice(1) : path;
        if (preferRelative) {
          return `src=${quote}/storage/${normalized}${quote}`;
        }
        if (SERVER_URL) {
          return `src=${quote}${SERVER_URL}/storage/${normalized}${quote}`;
        }
        return match;
      };
      const replaceStorageUrl = (match, quote, path) => {
        const normalized = path.replace(/^\/?storage\//, "");
        if (preferRelative) {
          return `src=${quote}/storage/${normalized}${quote}`;
        }
        if (SERVER_URL) {
          return `src=${quote}${SERVER_URL}/storage/${normalized}${quote}`;
        }
        return match;
      };
      let withAbsolute = htmlString
        .replace(/src=(["'])(\/?iskal\/[^"']+)\1/g, replaceIskalPath)
        .replace(/src=(["'])(https?:\/\/[^"']+\/storage\/[^"']+)\1/g, (match, quote, url) => {
          const [, path = ""] = url.split("/storage/");
          return replaceStorageUrl(match, quote, `/storage/${path}`);
        })
        .replace(/src=(["'])(\/?storage\/[^"']+)\1/g, replaceStorageUrl);
      if (!addCrossOrigin) return withAbsolute;
      return withAbsolute.replace(/<img(?![^>]*\scrossorigin=)/g, '<img crossorigin="anonymous"');
    },
    async buildFullPdfGroups() {
      let groups = [];
      try {
        const response = await journalContentGroupApi.showAll({ journal_idx: this.journal_idx });
        if (response.status === 200) {
          groups = response.data.response || [];
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      }
      const results = await Promise.all(
        groups.map(async (group) => {
          try {
            const response = await journalContentApi.showContentsByGroup({ group_idx: group.idx });
            if (response.status === 200) {
              return {
                ...group,
                contents: (response.data.response || []).filter(
                  (paper) =>
                    paper.category !== "empty" &&
                    paper.title !== "공페이지" &&
                    paper.title !== "빈페이지"
                ),
              };
            }
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error(error);
          }
          return { ...group, contents: [] };
        })
      );
      this.fullPdfGroups = results;
    },
    buildFullPdfPages() {
      const pages = [];
      let pageNumber = 1;

      this.fullPdfGroups.forEach((group, groupIndex) => {
        (group.contents || []).forEach((paper, paperIndex) => {
          const paperData = this.getFullPdfPaperData(paper);
          const abstractHtml = this.buildAbstractHtml(paperData);
          const abstractPages = abstractHtml
            ? splitContentByHeight(
                abstractHtml,
                this.pageSize,
                this.getAbstractCoverHeightForPaper(paperData),
                {
                  containerStyle: 'font-family: "KoPubWorldBatang", "Noto Serif KR", serif; font-size: 10pt; line-height: 1.8; text-align: justify;',
                  paragraphStyle: "margin: 0 0 10px 0;",
                }
              )
            : [""];
          const contentHtml = this.processImageUrls(this.normalizeContentToHtml(paperData?.content));
          const contentPages = contentHtml ? splitContentByHeight(contentHtml, this.pageSize, 0) : [""];
          const splitComments = this.splitCommentsForPaper(paperData, contentPages);

          if (pageNumber % 2 === 0) {
            pages.push({
              key: `paper-${paperData.idx}-blank-${pageNumber}`,
              type: "blank",
              paper: paperData,
              group,
              pageNumber,
            });
            pageNumber += 1;
          }

          this.$set(this.fullPdfGroups[groupIndex].contents, paperIndex, {
            ...paperData,
            start_page: pageNumber,
          });

          pages.push({
            key: `paper-${paperData.idx}-cover`,
            type: "paper-cover",
            paper: paperData,
            group,
            comments: this.getCoverCommentsForPaper(paperData),
            pageNumber,
          });
          pageNumber += 1;

          abstractPages.forEach((pageHtml, index) => {
            pages.push({
              key: `paper-${paperData.idx}-abstract-${index}`,
              type: "abstract",
              paper: paperData,
              group,
              pageNumber,
              html: pageHtml,
              isFirst: index === 0,
              isLast: index === abstractPages.length - 1,
            });
            pageNumber += 1;
          });

          contentPages.forEach((pageHtml, index) => {
            pages.push({
              key: `paper-${paperData.idx}-content-${index}`,
              type: "content",
              paper: paperData,
              group,
              pageNumber,
              html: pageHtml,
              comments: splitComments[index] || [],
            });
            pageNumber += 1;
          });
        });
      });

      if (pages.length > 0) {
        const backPagesCount = ["inner_back", "backcover"].reduce((count, pageId) => (
          count + (this.isFixedPageEnabled(pageId) ? 1 : 0)
        ), 0);
        const lastPageNumber = pageNumber - 1 + backPagesCount;
        if (lastPageNumber % 2 !== 0) {
          pages.push({
            key: `final-blank-${pageNumber}`,
            type: "blank",
            paper: {},
            group: {},
            pageNumber,
          });
          pageNumber += 1;
        }
      }

      this.fullPdfPages = pages;
    },
    getFullPdfPaperData(paper) {
      return paper;
    },
    buildAbstractHtml(paper) {
      const source = paper?.abstract || paper?.abstract_eng || "";
      if (!source) return "";
      return this.processImageUrls(renderAstToHtml(parseContentToAst(source)));
    },
    splitCommentsForPaper(paper, contentPages) {
      const comments = paper?.comments || [];
      if (!comments.length || !contentPages.length) {
        return contentPages.map(() => []);
      }
      const result = contentPages.map(() => []);
      contentPages.forEach((pageContent, pageIndex) => {
        const markerRegex = /data-footnote-id="(\d+)"|<sup[^>]*>\^?(\d+)\)?<\/sup>/g;
        let match;
        while ((match = markerRegex.exec(pageContent)) !== null) {
          const footnoteId = match[1] || match[2];
          const comment = comments.find(c => {
            const num = c.superscript?.toString().replace(/\D/g, "");
            return num === footnoteId;
          });
          if (comment && !result[pageIndex].includes(comment)) {
            result[pageIndex].push(comment);
          }
        }
      });
      return result;
    },
    getCoverCommentsForPaper(paper) {
      const comments = paper?.comments || [];
      if (!comments.length) return [];
      const coverTextParts = [
        paper?.title,
        paper?.title_eng,
      ];
      (paper?.authors || []).forEach((author) => {
        coverTextParts.push(author?.full_name);
        coverTextParts.push(author?.name_eng);
        coverTextParts.push(author?.organization);
        coverTextParts.push(author?.organization_eng);
      });
      const coverText = coverTextParts.filter(Boolean).join(" ");
      const markerComments = this.collectCommentsByMarkers(coverText, comments);
      const titlePageComments = comments.filter((comment) =>
        this.isTitlePageComment(comment)
      );
      if (!titlePageComments.length) return markerComments;
      const combined = [...markerComments];
      titlePageComments.forEach((comment) => {
        if (!combined.includes(comment)) {
          combined.push(comment);
        }
      });
      return combined;
    },
    collectCommentsByMarkers(text, comments) {
      if (!text || !comments?.length) return [];
      const markerRegex = /data-footnote-id="(\d+)"|<sup[^>]*>\^?(\d+)\)?<\/sup>|\^(\d+)/g;
      const result = [];
      const used = new Set();
      let match;
      while ((match = markerRegex.exec(text)) !== null) {
        const footnoteId = match[1] || match[2] || match[3];
        const comment = comments.find((c) => {
          const num = c.superscript?.toString().replace(/\D/g, "");
          return num === footnoteId;
        });
        if (comment && !used.has(comment.idx)) {
          used.add(comment.idx);
          result.push(comment);
        }
      }
      return result;
    },
    isTitlePageComment(comment) {
      if (!comment) return false;
      if (comment.page === "title_page") return true;
      return comment.superscript === "*" && Number(comment.page) === 1;
    },
    normalizeContentToHtml(rawContent) {
      if (!rawContent) return "";
      if (typeof rawContent !== "string") {
        return rawContent;
      }
      const trimmed = rawContent.trim();
      if (!trimmed) return "";
      if (trimmed.startsWith("{")) {
        try {
          const parsed = JSON.parse(trimmed);
          if (parsed && parsed.version && Array.isArray(parsed.blocks)) {
            return renderAstToHtml(parsed);
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.warn("content JSON parse failed", error);
        }
      }
      return rawContent;
    },
    getAbstractCoverHeightForPaper(paper) {
      if (typeof window === "undefined" || typeof document === "undefined") {
        return 0;
      }

      const contentWidth = getContentWidth(this.pageSize, true);
      const container = document.createElement("div");
      container.style.cssText = `
        position: absolute;
        visibility: hidden;
        width: ${contentWidth}px;
        padding: 0;
      `;

      const titleEl = document.createElement("div");
      titleEl.textContent = "● 초록(Abstract)";
      titleEl.style.cssText = `
        font-size: 12pt;
        font-weight: bold;
        margin: 0 0 16px 0;
      `;
      container.appendChild(titleEl);

      let keywordReserve = 0;
      if (paper?.keyword || paper?.keyword_eng) {
        const keywordWrap = document.createElement("div");
        keywordWrap.style.cssText = `
          font-size: 9pt;
          line-height: 1.4;
        `;
        const keywordLabel = document.createElement("span");
        keywordLabel.textContent = "핵심어";
        keywordLabel.style.cssText = `
          display: inline-block;
          padding: 2px 8px;
          font-size: 9pt;
        `;
        const keywordText = document.createElement("span");
        keywordText.textContent = paper.keyword || paper.keyword_eng || "";
        keywordText.style.cssText = "font-size: 9pt;";
        keywordWrap.appendChild(keywordLabel);
        keywordWrap.appendChild(keywordText);
        container.appendChild(keywordWrap);
      }

      document.body.appendChild(container);
      const titleStyles = window.getComputedStyle(titleEl);
      const titleHeight =
        titleEl.offsetHeight +
        (parseFloat(titleStyles.marginTop) || 0) +
        (parseFloat(titleStyles.marginBottom) || 0);

      if (paper?.keyword || paper?.keyword_eng) {
        const keywordWrap = container.lastChild;
        const keywordHeight = keywordWrap ? keywordWrap.offsetHeight : 0;
        const keywordBottom = 60;
        keywordReserve = keywordHeight + keywordBottom;
      }

      document.body.removeChild(container);
      return Math.ceil(titleHeight + keywordReserve);
    },
    getFixedPagesSettings() {
      try {
        if (this.journal?.fixed_pages_info) {
          const settings = JSON.parse(this.journal.fixed_pages_info);
          if (settings && typeof settings === "object") {
            return settings;
          }
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn("fixed_pages_info 파싱 실패", error);
      }
      return {};
    },
    isFixedPageEnabled(pageId) {
      const settings = this.getFixedPagesSettings();
      if (Array.isArray(settings)) {
        const frontPageIds = ["cover", "info", "inner_front", "blank", "title", "toc"];
        if (frontPageIds.includes(pageId)) {
          return settings.some((page) => page?.id === pageId);
        }
        return true;
      }
      if (Object.prototype.hasOwnProperty.call(settings, pageId)) {
        return settings[pageId] !== false;
      }
      return true;
    },
  },
  computed: {
    dimensions() {
      return PAGE_DIMENSIONS[this.pageSize];
    },
    fullPdfItems() {
      return this.fullPdfGroups.reduce((acc, group) => acc.concat(group.contents || []), []);
    },
    totalPageCount() {
      let lastPage = this.fixedPagesList.length;
      this.groups.forEach((group) => {
        group.contents?.forEach((content) => {
          if (content.endPage && content.endPage > lastPage) {
            lastPage = content.endPage;
          }
        });
      });

      return lastPage + this.fixedPages.length;
    },
    fixedPages() {
      // 마지막 논문의 endPage를 찾아서 뒤쪽 페이지 계산
      let lastPage = this.fixedPagesList.length;
      this.groups.forEach(group => {
        group.contents?.forEach(content => {
          if (content.endPage && content.endPage > lastPage) {
            lastPage = content.endPage;
          }
        });
      });

      // EditGroupPapers에서 저장한 설정 확인
      let enabledSettings = {};
      try {
        if (this.journal?.fixed_pages_info) {
          const parsed = JSON.parse(this.journal.fixed_pages_info);
          if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
            enabledSettings = parsed;
          }
        }
      } catch (e) {
        // 파싱 실패시 기본값 사용
      }

      // 뒤쪽 고정 페이지 (enabled 설정 반영)
      const backPages = [];
      let pageNum = lastPage + 1;

      if (enabledSettings.inner_back !== false) {
        backPages.push({ id: 'inner_back', title: "속표지(뒷)", page: pageNum++, isBlank: false });
      }
      if (enabledSettings.backcover !== false) {
        backPages.push({ id: 'backcover', title: "백커버", page: pageNum++, isBlank: false });
      }

      return backPages;
    },
  },
  async mounted() {
    await this.fetchJournal();

    // journal 데이터에서 fixedPagesList 불러오기
    this.loadFixedPagesListFromJournal();

    await this.getJournalGroups();
    this.createJournalIndex();

    // 페이지 번호 재계산
    this.recalculatePageNumbers();

    await this.ensureFullPdfReady();
  },
  watch: {
    groups: {
      deep: true,
      handler(newGroups) {
        if (
          newGroups.some((group) =>
            group.contents.some((content) => content.startPage !== undefined)
          )
        ) {
          this.createJournalIndex();
        }
      },
    },
  },
};
</script>

<style scoped>
</style>
